import { useTranslation } from '@/hooks/useTranslation';
import { Modal } from './Modal';
import { useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useModalContext } from '@/hooks/useModalContext';
import { toast } from 'sonner';
import { LoadingIndicator } from '../common/LoadingIndicator';
import { ErrorBag } from '@/types/error';
import { parseAxiosError } from '@/utils/error';
import { FormErrorDisplay } from '../common/FormErrorDisplay';
import { useCreateApiKey } from '@/hooks/organization/apiKey/useCreateApiKey';
import { ApiKey } from '@/types/organization';
import { Checkbox } from '../ui/checkbox';

export const CreateApiKeyModal = () => {
  const { t } = useTranslation('common');
  const { closeModal } = useModalContext();
  const [errors, setErrors] = useState<ErrorBag>({});

  const handleClose = (isSuccess?: boolean) => {
    closeModal('createApiKey', isSuccess ?? false);
  };

  const [name, setName] = useState('');
  const [abilities, setAbilities] = useState<string[]>(['read']);

  const { mutate: createList, isPending: isCreatingList } = useCreateApiKey({
    onSuccess: () => {
      toast.success(t('generalSave'));
      handleClose(true);
      setName('');
      setAbilities(['read']);
    },
    onError: (error) => {
      const parsedErrors = parseAxiosError(error);
      if (parsedErrors) {
        setErrors(parsedErrors.errors);
      }
      toast.error(t('generalFailed'));
    },
  });

  const handleSave = () => {
    const data: Partial<ApiKey> = {
      name,
      abilities,
    };

    createList(data);
  };

  const handleAbilityChange = ({ ability, checked }: { ability: string, checked: boolean }) => {
    setAbilities(value => {
      let abilities = value;
      if (checked && !abilities.includes(ability)) {
        abilities = [...abilities, ability]
      }

      if (!checked) {
        abilities = abilities.filter(item => item !== ability);
      } 

      return abilities;
    });
  };

  return (
    <Modal modalType="createApiKey" title={t('createApiKey')}
    >
      <div className="space-y-4">
        <div className="relative">
          <Label htmlFor="list-name">{t('name')}</Label>
          <Input
            id="list-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormErrorDisplay field="name" error={errors?.name} />
        </div>

        <div className="relative">
          <Label htmlFor="list-name">{t('abilities')}</Label>
          <div className="flex gap-6">
            <div className="relative flex w-full items-start gap-2 rounded-lg border border-input p-4 shadow-sm shadow-black/5 has-[[data-state=checked]]:border-ring">
              <Checkbox
                id="checkbox-14"
                className="order-1 after:absolute after:inset-0"
                aria-describedby="checkbox-14-description"
                checked={abilities.includes('read')}
                onCheckedChange={checked => handleAbilityChange({ ability: 'read', checked: Boolean(checked) })}
              />
              <div className="flex grow items-start gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="checkbox-14">
                    {t('apiKey.abilities.read')}
                  </Label>
                  {/* <p id="checkbox-14-description" className="text-xs text-muted-foreground">
                    A short description goes here.
                  </p> */}
                </div>
              </div>
            </div>

            <div className="relative flex w-full items-start gap-2 rounded-lg border border-input p-4 shadow-sm shadow-black/5 has-[[data-state=checked]]:border-ring">
              <Checkbox
                id="checkbox-14"
                className="order-1 after:absolute after:inset-0"
                aria-describedby="checkbox-14-description"
                checked={abilities.includes('write')}
                onCheckedChange={checked => handleAbilityChange({ ability: 'write', checked: Boolean(checked) })}
              />
              <div className="flex grow items-start gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="checkbox-14">
                    {t('apiKey.abilities.write')}
                  </Label>
                  {/* <p id="checkbox-14-description" className="text-xs text-muted-foreground">
                    A short description goes here.
                  </p> */}
                </div>
              </div>
            </div>
          </div>
          <FormErrorDisplay field="name" error={errors?.name} />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => handleClose()}>
            {t('close')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isCreatingList}
            className="flex gap-1"
            variant="conversionCraft"
          >
            {isCreatingList && <LoadingIndicator size="small" />}
            {t('save')}
          </Button>
        </DialogFooter>
      </div>
    </Modal>
  );
};
